import { createReducer } from 'typesafe-actions';
import { DisclosureCarriers } from '../../../components/FormComponents/Components/Disclosures/Disclosures.types';
import {
  AdditionalInterestAndInsured,
  BuildingAddress,
  CoverageOptions,
} from '../../../configs/APISchemas/Application.schema';
import { PaymentPlan } from '../../../configs/APISchemas/QuoteSchema';
import { validatePurchasePolicyLocalStorage } from '../../../helpers/validateLocalStorage';
import {
  _resetPurchasePolicy,
  _resetPurchasePolicyFlow,
  _updateCommunityId,
  _updatePurchasePolicyAdditionalData,
  _updatePurchasePolicyFlow,
  _updatePurchasePolicyInput,
  _updateUnderwritingCompanyId,
  updatePaymentPlans,
} from '../../actions/purchasePolicy';
import { SharedInput } from './types';

export interface PurchasePolicyOptionalCoverages {
  fireDepartmentService: boolean;
  sinkHole: boolean;
  ordinanceOrLaw: boolean;
  rentalIncome: boolean;
  workersCompensation: boolean;
}

export interface PurchasePolicyInput extends SharedInput {
  phone: string;
  coverageStart: string | null;
  coverageEnd: string | null;

  personalPropertyCoverage: number;
  personalLiabilityCoverage: number;
  medicalPayments: number;
  deductible: number;
  heading: number;
  waterBackup: number;
  petDamage: number;
  lossOfUse: number;
  theft: number;
  identityFraud: number;
  windHail: number;
  earthquake: number;
  hurricane: number;

  additionalInterests: AdditionalInterestAndInsured[];
  buildingAddress: BuildingAddress;
  additionalInsuredRoommates: AdditionalInterestAndInsured[];

  optionalCoverages: PurchasePolicyOptionalCoverages;
}

export interface PurchasePolicyFlow {
  isClickedStartApplicationContinue: boolean;
  isClickedQuoteInformationContinue: boolean;
  hasVisitedCoverageDetails: boolean;
}

export interface PurchasePolicyAdditionalData {
  paymentPlans: PaymentPlan[] | null;
  communityId: string | null;
  underwritingCompanyId: string;
  underwritingCompanyName: string;
  id: string;
  purchased: boolean;
  landlord_email: string;
  subscription: string;
  policyNumber: string;
  fixedBuilding: boolean;
  carrier: DisclosureCarriers;
  coverageOptions: CoverageOptions | [];
  companyProductCd: string;
  leaseId: string | null;
  buildingUnitId: string | null;
  residentId: string | null;
}

export interface PurchasePolicy {
  input: PurchasePolicyInput;
  flow: PurchasePolicyFlow;
  additionalData: PurchasePolicyAdditionalData;
}

export const optionalCoveragesDefaultValue = {
  fireDepartmentService: false,
  sinkHole: false,
  ordinanceOrLaw: false,
  rentalIncome: false,
  workersCompensation: false,
};

export const buildingAddressDefaultValue = {
  label: '',
  value: {
    state: '',
    postalCode: '',
    municipality: '',
    street: '',
    addressNumber: '',
  },
};

export const addressDefaultValue = {
  state: '',
  postalCode: '',
  municipality: '',
  street: '',
  shortNameStreet: '',
  addressNumber: '',
};

const localStorageData = validatePurchasePolicyLocalStorage();

const initialState: PurchasePolicy = {
  input: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    unit: {
      id: '',
      name: '',
    },
    addressName: '',
    address: addressDefaultValue,
    buildingUnit: '',
    buildingId: '',
    buildingName: '',
    buildingAddress: buildingAddressDefaultValue,
    coverageStart: null,
    coverageEnd: null,
    personalPropertyCoverage: 0,
    personalLiabilityCoverage: 0,
    medicalPayments: 0,
    deductible: 0,
    heading: 0,
    waterBackup: 0,
    petDamage: 0,
    lossOfUse: 0,
    theft: 0,
    windHail: 0,
    identityFraud: 0,
    earthquake: 0,
    hurricane: 0,
    additionalInterests: [],
    additionalInsuredRoommates: [],
    optionalCoverages: optionalCoveragesDefaultValue,
    ...(localStorageData.input || {}),
  },
  flow: {
    isClickedStartApplicationContinue: false,
    isClickedQuoteInformationContinue: false,
    hasVisitedCoverageDetails: false,
    ...(localStorageData.flow || {}),
  },
  additionalData: {
    paymentPlans: null,
    communityId: null,
    underwritingCompanyId: '',
    underwritingCompanyName: '',
    id: '',
    purchased: false,
    landlord_email: '',
    subscription: '',
    policyNumber: '',
    fixedBuilding: false,
    carrier: null,
    coverageOptions: [],
    companyProductCd: '',
    buildingUnitId: null,
    leaseId: null,
    residentId: null,
    ...(localStorageData.additionalData || {}),
  },
};

export const purchasePolicyReducer = createReducer<PurchasePolicy>(initialState)
  .handleAction(
    _updatePurchasePolicyInput,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof _updatePurchasePolicyInput>,
    ): PurchasePolicy => ({
      ...state,
      input: {
        ...state.input,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    _resetPurchasePolicyFlow,
    (state: PurchasePolicy): PurchasePolicy => ({
      ...state,
      flow: initialState.flow,
    }),
  )
  .handleAction(
    _updatePurchasePolicyFlow,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof _updatePurchasePolicyFlow>,
    ): PurchasePolicy => ({
      ...state,
      flow: {
        ...state.flow,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    _updatePurchasePolicyAdditionalData,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof _updatePurchasePolicyAdditionalData>,
    ): PurchasePolicy => ({
      ...state,
      additionalData: {
        ...state.additionalData,
        ...action.payload,
      },
    }),
  )
  .handleAction(
    updatePaymentPlans,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof updatePaymentPlans>,
    ): PurchasePolicy => ({
      ...state,
      additionalData: {
        ...state.additionalData,
        paymentPlans: action.payload,
      },
    }),
  )
  .handleAction(
    _updateCommunityId,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof _updateCommunityId>,
    ): PurchasePolicy => ({
      ...state,
      additionalData: {
        ...state.additionalData,
        communityId: action.payload,
      },
    }),
  )
  .handleAction(
    _updateUnderwritingCompanyId,
    (
      state: PurchasePolicy,
      action: ReturnType<typeof _updateUnderwritingCompanyId>,
    ): PurchasePolicy => ({
      ...state,
      additionalData: {
        ...state.additionalData,
        underwritingCompanyId: action.payload,
      },
    }),
  )
  .handleAction(_resetPurchasePolicy, (): PurchasePolicy => initialState);
